/**
 * Content-Security-Policy for the SPA (injected before other app code runs).
 *
 * Env (CRA): set in .env / .env.development / .env.production
 *
 * - REACT_APP_CSP_CONNECT_SRC_EXTRA — optional space-separated origins to allow
 *   for fetch/XHR/WebSocket (e.g. "http://localhost:8000 http://127.0.0.1:8000").
 *   If unset, origins are derived from REACT_APP_PVL_APP_URL and
 *   REACT_APP_PVL_INTEGRATION_URL (localhost gets both hostname and 127.0.0.1).
 */

function parseOrigin(urlString) {
  if (!urlString || typeof urlString !== "string") return null;
  const trimmed = urlString.trim();
  if (!trimmed) return null;
  try {
    return new URL(trimmed).origin;
  } catch {
    return null;
  }
}

function localhostTwin(origin) {
  if (!origin) return [];
  try {
    const u = new URL(origin);
    if (u.hostname === "localhost") {
      return [origin, origin.replace("://localhost", "://127.0.0.1")];
    }
    if (u.hostname === "127.0.0.1") {
      return [origin, origin.replace("://127.0.0.1", "://localhost")];
    }
  } catch {
    /* ignore */
  }
  return [origin];
}

function buildConnectSrcExtra() {
  const explicit = process.env.REACT_APP_CSP_CONNECT_SRC_EXTRA;
  if (explicit != null && String(explicit).trim() !== "") {
    return String(explicit)
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .join(" ");
  }

  const origins = new Set();
  const appUrl = process.env.REACT_APP_PVL_APP_URL;
  const integrationUrl = process.env.REACT_APP_PVL_INTEGRATION_URL;

  for (const raw of [appUrl, integrationUrl]) {
    const o = parseOrigin(raw);
    if (o) {
      localhostTwin(o).forEach((x) => origins.add(x));
    }
  }

  return [...origins].join(" ");
}

function buildCspPolicy() {
  const connectExtra = buildConnectSrcExtra();
  const connectSrc = [
    "'self'",
    connectExtra,
    "https://sc-ie-api.sureconnect.ai",
    "https://dev-sc-ie-api.phoneverify.ai",
    "https://api.stripe.com",
    "https://www.googletagmanager.com",
    "https://www.google-analytics.com",
    "https://www.googleapis.com",
    "https://www.google.com",
    "https://graph.microsoft.com",
    "https://*.crisp.chat",
    "wss://*.crisp.chat",
    "https://*.mixpanel.com",
  ]
    .filter(Boolean)
    .join(" ");

  return [
    "default-src 'self'",
    "script-src 'self' 'unsafe-inline' 'unsafe-eval' https://js.stripe.com https://www.googletagmanager.com https://www.google.com https://www.gstatic.com https://accounts.google.com",
    "style-src 'self' 'unsafe-inline' https://cdn.phoneverify.ai https://fonts.googleapis.com",
    "font-src 'self' data: https://cdn.phoneverify.ai https://fonts.gstatic.com",
    "img-src 'self' data: https: blob:",
    `connect-src ${connectSrc}`,
    "frame-src 'self' https://www.googletagmanager.com https://www.youtube.com https://js.stripe.com https://www.google.com",
    "object-src 'none'",
    "base-uri 'self'",
    "form-action 'self'",
    "upgrade-insecure-requests",
  ].join("; ");
}

(function applyContentSecurityPolicy() {
  if (typeof document === "undefined") return;

  const existing = document.querySelector(
    'meta[http-equiv="Content-Security-Policy"]'
  );
  if (existing) {
    existing.remove();
  }

  const meta = document.createElement("meta");
  meta.setAttribute("http-equiv", "Content-Security-Policy");
  meta.setAttribute("content", buildCspPolicy());
  document.head.insertBefore(meta, document.head.firstChild);
})();
